// Reads the same localStorage-backed overrides written by /content-admin
// (pages/content-admin/adminStore.ts, key "motorpro_content_admin_v1") so
// every public landing component reflects admin edits live, after reload,
// and after "Restaurar contenido original" — without a backend or database.
// Read-only here — writing only ever happens from the admin panel.

import { useEffect, useState } from "react";
import { LINKS as DEFAULT_LINKS } from "../config/links";

const CONTENT_STORAGE_KEY = "motorpro_content_admin_v1";
const UPDATE_EVENT = "motorpro-content-updated";

function readFields(): Record<string, string> {
  try {
    const raw = localStorage.getItem(CONTENT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed?.fields || {};
  } catch {
    return {};
  }
}

function useLiveUpdates(resolve: () => void) {
  useEffect(() => {
    resolve();
    window.addEventListener("storage", resolve);
    window.addEventListener(UPDATE_EVENT, resolve);
    return () => {
      window.removeEventListener("storage", resolve);
      window.removeEventListener(UPDATE_EVENT, resolve);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function useContentField(key: string, fallback: string): string {
  const resolve = () => {
    const raw = readFields()[key];
    return raw === undefined || raw === "" ? fallback : raw;
  };
  const [value, setValue] = useState(resolve);
  useLiveUpdates(() => setValue(resolve()));
  return value;
}

export function useContentBoolean(key: string, fallback: boolean): boolean {
  const resolve = () => {
    const raw = readFields()[key];
    return raw === undefined ? fallback : raw === "true";
  };
  const [value, setValue] = useState(resolve);
  useLiveUpdates(() => setValue(resolve()));
  return value;
}

export function useContentList(key: string, fallback: string[]): string[] {
  const resolve = () => {
    const raw = readFields()[key];
    if (raw === undefined || raw === "") return fallback;
    const lines = raw
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    return lines.length > 0 ? lines : fallback;
  };
  const [value, setValue] = useState(resolve);
  useLiveUpdates(() => setValue(resolve()));
  return value;
}

export function useContentJson<T>(key: string, fallback: T): T {
  const resolve = (): T => {
    const raw = readFields()[key];
    if (raw === undefined || raw === "") return fallback;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  };
  const [value, setValue] = useState<T>(resolve);
  useLiveUpdates(() => setValue(resolve()));
  return value;
}

export interface NavItem {
  label: string;
  href: string;
}

export function useNavItems(key: string, fallback: NavItem[]): NavItem[] {
  const resolve = (): NavItem[] => {
    const raw = readFields()[key];
    if (raw === undefined || raw === "") return fallback;
    const items = raw
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [label, href] = line.split("|").map((part) => part.trim());
        return { label, href };
      })
      .filter((item) => item.label && item.href);
    return items.length > 0 ? items : fallback;
  };
  const [value, setValue] = useState(resolve);
  useLiveUpdates(() => setValue(resolve()));
  return value;
}

type LinksShape = { [K in keyof typeof DEFAULT_LINKS]: string };

export function useLinks(): LinksShape {
  const resolve = () => {
    const fields = readFields();
    const pick = (fieldKey: string, fallback: string) => fields[fieldKey] || fallback;
    return {
      ...DEFAULT_LINKS,
      appUrl: pick("links.appUrl", DEFAULT_LINKS.appUrl),
      exploreUrl: pick("links.exploreUrl", DEFAULT_LINKS.exploreUrl),
      whatsappGeneral: pick("links.whatsappGeneral", DEFAULT_LINKS.whatsappGeneral),
      whatsappDealers: pick("links.whatsappDealers", DEFAULT_LINKS.whatsappDealers),
      facebook: pick("links.facebook", DEFAULT_LINKS.facebook),
      instagram: pick("links.instagram", DEFAULT_LINKS.instagram),
      tiktok: pick("links.tiktok", DEFAULT_LINKS.tiktok),
      privacyPolicy: pick("links.privacyPolicy", DEFAULT_LINKS.privacyPolicy),
      termsAndConditions: pick("links.termsAndConditions", DEFAULT_LINKS.termsAndConditions),
    };
  };
  const [value, setValue] = useState(resolve);
  useLiveUpdates(() => setValue(resolve()));
  return value;
}
